// Settings, opened from the avatar, in tabs: Site settings | Alerts | Ping | Account | Users (or Team).
// Site settings: one site picker driving the per-site cards, Site (name, domains, timezone,
// currency; admins also get its Danger zone), Alerts (which alert emails the site sends and to
// whom), Auto-fix (its GitHub repository and mode), Install (its script tags) and, for admins,
// Metric cards (metric-cards.ts). Shown to admins and owners; viewers of a site see a note instead
// of the cards. Admins also see Deleted sites.
// Alerts (admins only): workspace-wide alerts: the global error spike thresholds, failed events and
// auto-fix's daily cap and GitHub token status.
// Ping (admins only): push on this device, the Ping iOS app's devices, delivery settings, and sources
// with their tokens and snippets (notify-settings.ts). On ping.monoblock.ae only Ping and Account show.
// Account: the signed-in account, and their password (account-password.ts). Users (admins: who can sign in and each person's profile, a
// role plus client access) or Team (org owners: their viewers).
import { renderAlerts } from "./alerts";
import { renderAlertSettings } from "./alert-settings";
import { renderTeam, signOut, type Me } from "./team";
import { renderInstall } from "./install";
import { metricCardsEditor } from "./metric-cards";
import { renderAccountPassword } from "./account-password";
import { renderNotifySettings } from "./notify-settings";
import { renderAutofixGlobal, renderAutofixSettings } from "./autofix-settings";
import { renderDeletedSites, renderSiteCard, type SiteView } from "./site-settings";

type Role = "owner" | "viewer";

interface UserRow {
  id: string | null;
  email: string;
  admin: boolean;
  builtin: boolean;
  access: { org: string; role: Role }[];
  last_login_at: number | null;
  /** Has a password (else signs in with email links only). */
  password: boolean;
}

interface Org {
  id: string;
  name: string;
}

interface Opts {
  /** Where the Users card goes. */
  el: HTMLElement;
  /** The signed-in admin's email (they can't clear their own password here). */
  me: string;
  mobile: boolean;
  /** False once the user has moved on, so a slow load doesn't paint over another screen. */
  current: () => boolean;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

const ERRORS: Record<string, string> = {
  bad_email: "Enter a valid email address.",
  no_access: "Pick Admin, or give access to at least one client.",
  unknown_org: "One of those clients no longer exists. Reload and try again.",
  builtin_admin: "That address is a built-in admin and can't be changed here.",
  cant_demote_self: "You can't take away your own admin role.",
  cant_remove_self: "You can't remove yourself.",
  cant_clear_self: "Use Account to remove your own password.",
  no_password: "They don't have a password any more.",
};

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
  return out as T;
}

const seen = (t: number | null) => (t ? new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "Never");

function profile(u: UserRow, orgs: Org[]): string {
  if (u.admin) return `<span class="pill none">Admin</span>${u.builtin ? ` <span class="muted">built in</span>` : ""}`;
  const name = (id: string) => orgs.find((o) => o.id === id)?.name ?? id;
  return u.access.map((a) => `${esc(name(a.org))} <span class="muted">${a.role}</span>`).join(", ");
}

/** The add/edit form. `u` is null when adding. */
function editor(u: UserRow | null, orgs: Org[]): string {
  const roleOf = (org: string) => u?.access.find((a) => a.org === org)?.role ?? "";
  const admin = u?.admin ?? false;
  return `<form class="usr-edit" data-id="${esc(u?.id ?? "")}">
  ${
    u
      ? `<div class="usr-email">${esc(u.email)}</div>`
      : `<label class="fld"><span>Email</span><input class="acct-input" type="email" name="email" placeholder="name@company.com" required autocomplete="off"></label>`
  }
  <fieldset class="usr-role">
    <legend>Profile</legend>
    <label><input type="radio" name="kind" value="admin"${admin ? " checked" : ""}> <b>Admin</b> <span class="muted">sees every client and manages users</span></label>
    <label><input type="radio" name="kind" value="client"${admin ? "" : " checked"}> <b>Client access</b> <span class="muted">sees only the clients below</span></label>
  </fieldset>
  <div class="usr-orgs"${admin ? " hidden" : ""}>
    ${orgs
      .map(
        (o) => `<label class="usr-org"><span>${esc(o.name)}</span><select class="btn" name="org:${esc(o.id)}" aria-label="Access to ${esc(o.name)}">
      <option value=""${roleOf(o.id) ? "" : " selected"}>No access</option>
      <option value="viewer"${roleOf(o.id) === "viewer" ? " selected" : ""}>Viewer</option>
      <option value="owner"${roleOf(o.id) === "owner" ? " selected" : ""}>Owner</option>
    </select></label>`,
      )
      .join("")}
    <p class="muted usr-note">Viewers see the client's dashboards. Owners can also edit goals and funnels and invite viewers.</p>
  </div>
  <div class="usr-actions">
    <button class="btn primary" type="submit">${u ? "Save" : "Add user"}</button>
    <button class="btn" type="button" data-cancel>Cancel</button>
    <span class="acct-msg" role="status"></span>
  </div>
</form>`;
}

interface PageOpts {
  el: HTMLElement;
  mobile: boolean;
  current: () => boolean;
  me: Me;
  /** Every site (not org views) the person can see. */
  sites: { id: string; name: string }[];
  /** The site to open Site settings on: the one in the URL, else the toolbar's. May be an org view or empty. */
  site: string;
  /** Called when the person picks another site, so the choice lands in the URL. */
  onSite?: (id: string) => void;
  /** On ping.monoblock.ae: only Ping's settings and the account. */
  ping?: boolean;
  /** A site was renamed or otherwise saved, deleted or restored (Site card, Deleted sites): refresh the project list. */
  onSiteChanged?: (change: { saved?: SiteView; deleted?: string; restored?: string }) => void;
}

const roleLine = (me: Me) =>
  me.role === "admin"
    ? "Admin: sees every client and manages users."
    : me.orgs.map((o) => `${esc(o.name)} <span class="muted">${o.role}</span>`).join(", ");

/** Which site Site settings opens on: the asked-for one if it's a site, else the first editable, else the first. */
export function pickSite(sites: { id: string }[], wanted: string, canEdit: (id: string) => boolean): string | null {
  if (sites.some((s) => s.id === wanted)) return wanted;
  return (sites.find((s) => canEdit(s.id)) ?? sites[0])?.id ?? null;
}

type SettingsTab = "site" | "alerts" | "notifications" | "account" | "people";
/** The open tab, kept while the page is re-rendered (range changes, sign-in refresh). */
let settingsTab: SettingsTab = "site";
/** Opens Settings on this tab next time (Ping's "Create a source" goes to Settings → Ping). */
export const openSettingsTab = (t: SettingsTab) => void (settingsTab = t);

export async function renderSettingsPage(o: PageOpts): Promise<void> {
  const { me } = o;
  const canEdit = (id: string) => me.role === "admin" || !!me.can_edit?.includes(id);
  // Site settings only when there is something per-site this person may change.
  const showSites = !o.ping && o.sites.some((s) => canEdit(s.id));
  let site = pickSite(o.sites, o.site, canEdit);
  const siteName = (id: string | null) => o.sites.find((s) => s.id === id)?.name ?? "";
  const picker =
    o.sites.length > 1
      ? `<label class="set-site"><select class="btn" id="set-site" aria-label="Site">${o.sites
          .map((s) => `<option value="${esc(s.id)}"${s.id === site ? " selected" : ""}>${esc(s.name)}${canEdit(s.id) ? "" : " (view only)"}</option>`)
          .join("")}</select></label>`
      : `<span class="set-site set-site-one">${esc(siteName(site))}</span>`;
  const peopleTab = o.ping ? null : me.can_manage_users ? "Users" : me.can_manage_team ? "Team" : null;
  const showPing = me.role === "admin";
  const showAlerts = showPing && !o.ping;
  const tabs: [SettingsTab, string][] = [
    ...(showSites && site ? ([["site", "Site settings"]] as [SettingsTab, string][]) : []),
    ...(showAlerts ? ([["alerts", "Alerts"]] as [SettingsTab, string][]) : []),
    ...(showPing ? ([["notifications", "Ping"]] as [SettingsTab, string][]) : []),
    ["account", "Account"],
    ...(peopleTab ? ([["people", peopleTab]] as [SettingsTab, string][]) : []),
  ];
  if (!tabs.some(([t]) => t === settingsTab)) settingsTab = tabs[0]![0];
  o.el.innerHTML = `<div class="settings">
  <div class="err-head set-head"><h1>Settings</h1><span class="spacer"></span><div class="seg set-tabs" role="tablist" aria-label="Settings">${tabs
    .map(([t, l]) => `<button role="tab" id="set-tab-${t}" data-set-tab="${t}" aria-controls="set-panel-${t}" aria-selected="${t === settingsTab}" aria-pressed="${t === settingsTab}">${l}</button>`)
    .join("")}</div></div>
  ${
    showSites && site
      ? `<section class="set-sec" id="set-panel-site" role="tabpanel" aria-labelledby="set-tab-site"${settingsTab === "site" ? "" : " hidden"}>
    <div class="set-sec-head"><div class="set-sec-title"><p class="muted">Name, domains, alerts, auto-fix${me.role === "admin" ? ", install snippet and metric cards" : " and install snippet"} for one site.</p></div>${picker}</div>
    <div id="site-cards" class="set-cards"></div>
    ${me.role === "admin" ? `<div id="deleted-sites" class="set-cards"></div>` : ""}
  </section>`
      : ""
  }
  ${
    showAlerts
      ? `<section class="set-sec" id="set-panel-alerts" role="tabpanel" aria-labelledby="set-tab-alerts"${settingsTab === "alerts" ? "" : " hidden"}>
    <div class="set-sec-head"><div class="set-sec-title"><p class="muted">Alert settings for every site and app.</p></div></div>
    <div id="global-alerts-box"></div>
    <div id="global-autofix-box"></div>
  </section>`
      : ""
  }
  ${
    showPing
      ? `<section class="set-sec" id="set-panel-notifications" role="tabpanel" aria-labelledby="set-tab-notifications"${settingsTab === "notifications" ? "" : " hidden"}>
    <div class="set-sec-head"><div class="set-sec-title"><p class="muted">Ping: push notifications, the devices that get them, and the sources that send to it.</p></div></div>
    <div id="notify-box"></div>
  </section>`
      : ""
  }
  <section class="set-sec" id="set-panel-account" role="tabpanel" aria-labelledby="set-tab-account"${settingsTab === "account" ? "" : " hidden"}>
    <div class="card acct-card">
      <div class="card-head"><h2>Account</h2></div>
      <div class="acct-row"><span class="acct-avatar" aria-hidden="true">${esc((me.email[0] ?? "?").toUpperCase())}</span>
        <div class="acct-who"><div class="usr-email">${esc(me.email)}</div><div class="usr-profile">${roleLine(me)}</div></div>
        ${me.via === "dev" ? "" : `<button class="btn" data-signout>Sign out</button>`}
      </div>
      ${me.via === "dev" ? "" : `<div id="acct-password"></div>`}
    </div>
  </section>
  ${
    peopleTab
      ? `<section class="set-sec" id="set-panel-people" role="tabpanel" aria-labelledby="set-tab-people"${settingsTab === "people" ? "" : " hidden"}>
    <div id="users-box"></div>
    <div id="team-box"></div>
  </section>`
      : ""
  }
</div>`;
  o.el.querySelector("[data-signout]")?.addEventListener("click", () => void signOut(me));
  const pwBox = o.el.querySelector<HTMLElement>("#acct-password");
  if (pwBox) void renderAccountPassword(pwBox, me.email, o.current);
  for (const b of o.el.querySelectorAll<HTMLButtonElement>("[data-set-tab]"))
    b.addEventListener("click", () => {
      settingsTab = b.dataset.setTab as SettingsTab;
      for (const x of o.el.querySelectorAll<HTMLButtonElement>("[data-set-tab]")) {
        const on = x === b;
        x.setAttribute("aria-selected", String(on));
        x.setAttribute("aria-pressed", String(on));
      }
      for (const panel of o.el.querySelectorAll<HTMLElement>(".set-sec")) panel.hidden = panel.id !== `set-panel-${settingsTab}`;
    });

  const cards = o.el.querySelector<HTMLElement>("#site-cards");
  let siteGen = 0;
  const drawSite = () => {
    if (!cards || !site) return;
    const gen = ++siteGen;
    const current = () => o.current() && gen === siteGen;
    const s = { id: site, name: siteName(site) };
    if (!canEdit(s.id)) {
      cards.innerHTML = `<div class="card"><div class="acct-msg">You can see ${esc(s.name)}'s dashboards. Only its owners and MonoLog admins can change its alerts or get its install snippet.</div></div>`;
      return;
    }
    cards.innerHTML = `<div id="site-box"></div><div id="alerts-box"></div><div id="autofix-box"></div><div id="install-box"></div>${me.role === "admin" ? `<div id="mc-box"></div>` : ""}`;
    void renderSiteCard({
      el: cards.querySelector<HTMLElement>("#site-box")!,
      site: s,
      admin: me.role === "admin",
      current,
      onSaved: (v) => {
        // The picker and the other cards' headings use the name; the toolbar is the app's (onSiteChanged).
        const entry = o.sites.find((x) => x.id === v.id);
        if (entry) entry.name = v.name;
        const opt = o.el.querySelector<HTMLOptionElement>(`#set-site option[value="${CSS.escape(v.id)}"]`);
        if (opt) opt.textContent = v.name;
        const one = o.el.querySelector<HTMLElement>(".set-site-one");
        if (one) one.textContent = v.name;
        o.onSiteChanged?.({ saved: v });
      },
      onDeleted: (id) => o.onSiteChanged?.({ deleted: id }),
    });
    void renderAlerts({ el: cards.querySelector<HTMLElement>("#alerts-box")!, site: s, current });
    void renderAutofixSettings({ el: cards.querySelector<HTMLElement>("#autofix-box")!, site: s, current });
    void renderInstall(cards.querySelector<HTMLElement>("#install-box")!, s, current);
    const mc = cards.querySelector<HTMLElement>("#mc-box");
    if (mc) void metricCardsEditor(mc, s, current);
  };
  o.el.querySelector<HTMLSelectElement>("#set-site")?.addEventListener("change", (e) => {
    site = (e.target as HTMLSelectElement).value;
    o.onSite?.(site);
    drawSite();
  });
  if (cards && site) o.onSite?.(site);
  drawSite();
  const deleted = o.el.querySelector<HTMLElement>("#deleted-sites");
  if (deleted) void renderDeletedSites({ el: deleted, current: o.current, onRestored: (id) => o.onSiteChanged?.({ restored: id }) });

  const globalAlerts = o.el.querySelector<HTMLElement>("#global-alerts-box");
  if (globalAlerts) void renderAlertSettings({ el: globalAlerts, current: o.current });
  const globalAutofix = o.el.querySelector<HTMLElement>("#global-autofix-box");
  if (globalAutofix) void renderAutofixGlobal({ el: globalAutofix, current: o.current });
  const notify = o.el.querySelector<HTMLElement>("#notify-box");
  if (notify) void renderNotifySettings({ el: notify, current: o.current });

  const users = o.el.querySelector<HTMLElement>("#users-box");
  const team = o.el.querySelector<HTMLElement>("#team-box");
  if (me.can_manage_users && users) await renderSettings({ el: users, me: me.email, mobile: o.mobile, current: o.current });
  else if (me.can_manage_team && team) await renderTeam(team, me);
}

async function renderSettings(o: Opts): Promise<void> {
  const { el } = o;
  el.innerHTML = `<div class="card"><div class="acct-msg">Loading users…</div></div>`;
  let data: { users: UserRow[]; orgs: Org[] };
  try {
    data = await call("GET", "users");
  } catch (err) {
    if (!o.current()) return;
    el.innerHTML = `<div class="card"><div class="acct-msg error">Couldn't load users (${esc((err as Error).message)}).</div></div>`;
    return;
  }
  if (!o.current()) return;
  draw(o, data, null);
}

/** `editing`: a user id, "new", or null. */
function draw(o: Opts, data: { users: UserRow[]; orgs: Org[] }, editing: string | null): void {
  const { users, orgs } = data;
  const row = (u: UserRow) => {
    if (u.id && editing === u.id) return `<div class="usr-row editing">${editor(u, orgs)}</div>`;
    const self = u.email === o.me;
    const clear = u.id && u.password && !self ? `<button class="link danger" data-clear-pw="${esc(u.id)}" data-email="${esc(u.email)}">Clear password</button>` : "";
    const actions = u.builtin || !u.id ? clear : `<button class="link" data-edit="${esc(u.id)}">Edit</button>${clear}<button class="link danger" data-remove="${esc(u.id)}" data-email="${esc(u.email)}">Remove</button>`;
    const how = `<span class="usr-how">${u.password ? "Password set" : "Email link only"}</span>`;
    return `<div class="usr-row"><span class="usr-email">${esc(u.email)}</span><span class="usr-profile">${profile(u, orgs)}</span><span class="usr-seen">${seen(u.last_login_at)}${how}</span><span class="usr-act">${actions}</span></div>`;
  };
  o.el.innerHTML = `
  <div class="card">
    <div class="card-head"><h2>Users</h2><span class="spacer" style="flex:1"></span>${editing === "new" ? "" : `<button class="btn primary" data-add>Add user</button>`}</div>
    <p class="muted usr-intro">Everyone who can sign in to the dashboard, and what they can see. People sign in with their email; MonoLog emails them a sign-in link, or they can set a password in Settings → Account.</p>
    ${editing === "new" ? `<div class="usr-row editing">${editor(null, orgs)}</div>` : ""}
    <div class="usr-list">
      ${o.mobile ? "" : `<div class="usr-row usr-rh"><span>Email</span><span>Profile</span><span>Sign-in</span><span></span></div>`}
      ${users.map(row).join("")}
    </div>
  </div>`;
  wire(o, data);
}

function wire(o: Opts, data: { users: UserRow[]; orgs: Org[] }): void {
  const { el } = o;
  const reload = async (editing: string | null = null) => {
    const fresh = await call<{ users: UserRow[]; orgs: Org[] }>("GET", "users");
    if (o.current()) draw(o, fresh, editing);
  };
  el.querySelector("[data-add]")?.addEventListener("click", () => draw(o, data, "new"));
  for (const b of el.querySelectorAll<HTMLButtonElement>("[data-edit]")) b.addEventListener("click", () => draw(o, data, b.dataset.edit!));
  for (const b of el.querySelectorAll<HTMLButtonElement>("[data-remove]")) {
    b.addEventListener("click", async () => {
      if (b.dataset.confirm !== "1") {
        b.dataset.confirm = "1";
        b.textContent = `Remove ${b.dataset.email}?`;
        return;
      }
      try {
        await call("DELETE", `users/${b.dataset.remove}`);
        await reload();
      } catch (err) {
        b.textContent = ERRORS[(err as Error).message] ?? `Couldn't remove (${(err as Error).message})`;
      }
    });
  }
  for (const b of el.querySelectorAll<HTMLButtonElement>("[data-clear-pw]")) {
    b.addEventListener("click", async () => {
      if (b.dataset.confirm !== "1") {
        b.dataset.confirm = "1";
        b.textContent = `Clear ${b.dataset.email}'s password?`;
        return;
      }
      try {
        await call("DELETE", `users/${b.dataset.clearPw}/password`);
        await reload();
      } catch (err) {
        b.textContent = ERRORS[(err as Error).message] ?? `Couldn't clear (${(err as Error).message})`;
      }
    });
  }
  const form = el.querySelector<HTMLFormElement>(".usr-edit");
  if (!form) return;
  const orgsBox = form.querySelector<HTMLElement>(".usr-orgs")!;
  for (const r of form.querySelectorAll<HTMLInputElement>('input[name="kind"]')) r.addEventListener("change", () => (orgsBox.hidden = r.value === "admin" && r.checked));
  form.querySelector("[data-cancel]")!.addEventListener("click", () => draw(o, data, null));
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const f = new FormData(form);
    const admin = f.get("kind") === "admin";
    const access = admin
      ? []
      : data.orgs.map((org) => ({ org: org.id, role: String(f.get(`org:${org.id}`) ?? "") })).filter((a) => a.role === "viewer" || a.role === "owner");
    const msg = form.querySelector<HTMLElement>("[role=status]")!;
    const id = form.dataset.id;
    try {
      if (id) await call("PUT", `users/${id}`, { admin, access });
      else await call("POST", "users", { email: String(f.get("email") ?? ""), admin, access });
      await reload();
    } catch (err) {
      msg.textContent = ERRORS[(err as Error).message] ?? `Something went wrong (${(err as Error).message}).`;
      msg.className = "acct-msg error";
    }
  });
  form.querySelector<HTMLInputElement>('input[name="email"]')?.focus();
}
